import { useEffect, useState } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import { CreateCampaignModal } from "features/campaigns/components";
import { Banners, ContentContainer } from "features/layout/components";
import { ManageSubscriptionDialog } from "features/subscriptions/components";
import useSubscriptions from "features/subscriptions/hooks/useSubscriptions";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import Loader from "common/components/Loader";
import WebPushPrompt from "common/components/WorkspaceLayoutContainer/WebPushPrompt";
import {
  SelectedWorkspaceProvider,
  useSelectedWorkspaceContext,
} from "common/context/SelectedWorkspaceContext";
import useWorkspaces from "common/datahooks/useWorkspaces";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useAppStore from "common/hooks/useAppStore";
import useGTM from "common/hooks/useGTM";
import useWorkspaceChangeUpdates from "common/hooks/useWorkspaceChangeUpdates";
import type { SubscriptionType, UserPermissions } from "common/types";

import { workspaceRoutes } from "../../../pages/routes";
import SubscriptionActivation from "./SubscriptionActivation";
import WorkspaceLayout from "./WorkspaceLayout";

import NotFoundImage from "assets/images/404.png";

function Content({
  isLoading,
  errorComponent,
}: {
  isLoading: boolean;
  errorComponent: JSX.Element;
}) {
  useWorkspaceChangeUpdates();
  const { sendEvent } = useGTM();
  const location = useLocation();
  const workspace = useSelectedWorkspaceContext();
  const isCampaignModalOpen = useAppStore((state) => state.isCampaignModalOpen);
  const setIsCampaignModalOpen = useAppStore(
    (state) => state.setIsCampaignModalOpen,
  );
  const [manageSubscriptionState, setManageSubscriptionState] = useState<{
    isOpen: boolean;
    subscriptionType: SubscriptionType;
  }>({
    isOpen: false,
    subscriptionType: "agency",
  });
  const subscriptions = useSubscriptions();

  const isSettingsPage = location.pathname.includes("settings");

  useEffect(() => {
    if (!isSettingsPage) {
      useAppStore.getState().setPreviousLocation(location);
    }

    window.scrollTo({ top: 0 });

    const pagePath = workspace
      ? location.pathname.replace(`/workspaces/${workspace.id}`, "")
      : location.pathname;

    if (pagePath) {
      sendEvent("virtualPageView", () => ({
        pageUrl: window.location.href,
        pagePath,
      }));
    }
  }, [location]);

  let content = <Outlet />;
  let bannersComponent: JSX.Element;

  const currentRoute = workspaceRoutes.find(({ path }) =>
    location.pathname.includes(path.replace("/*", "").split("/")[0]),
  );

  if (errorComponent) {
    content = <ContentContainer>{errorComponent}</ContentContainer>;
  } else if (isLoading) {
    content = (
      <ContentContainer>
        <Loader />
      </ContentContainer>
    );
  } else if (
    currentRoute?.visibleForPermissions &&
    !currentRoute.visibleForPermissions.every(
      (requiredPermission) =>
        workspace.permissions.includes(requiredPermission) ||
        //if the required permission is limited, full permission passes
        (requiredPermission.endsWith("_limited")
          ? workspace.permissions.includes(
              requiredPermission.replace(
                "_limited",
                "_full",
              ) as UserPermissions[number],
            )
          : false),
    )
  ) {
    content = (
      <ContentContainer>
        <EmptyPlaceholder
          title="Page not found"
          subtitle="The page you are looking for is not available"
          imageSrc={NotFoundImage}
        />
      </ContentContainer>
    );
  }

  let isLocked = false;
  if (!isSettingsPage) {
    const {
      subscription_status: subscriptionStatus,
      lifetime_license_count: lifetimeLicenseCount,
      has_active_rental_subscription,
      agency_subscription_status,
      is_supplier,
    } = workspace;

    isLocked =
      !is_supplier &&
      !getWhitelabelCustomization() &&
      lifetimeLicenseCount === 0 &&
      !has_active_rental_subscription &&
      (!subscriptionStatus || subscriptionStatus === "cancelled") &&
      (!agency_subscription_status ||
        agency_subscription_status === "cancelled");

    if (isLocked) {
      content = (
        <SubscriptionActivation
          openManageSubscription={(subscriptionType) =>
            setManageSubscriptionState({ isOpen: true, subscriptionType })
          }
        />
      );
    } else {
      bannersComponent = <Banners />;
    }
  }

  return (
    <>
      {/* since we don't have an index route atm, this redirects index route to first workspace route */}
      {/* e.g.: xxx/workspaces/1aa4c82b-cdf8-417e-bcda-966d661f981a/ */}
      <Routes>
        <Route
          path=""
          element={<Navigate to={workspaceRoutes[0].path} replace />}
        />
      </Routes>
      <WorkspaceLayout bannersComponent={bannersComponent}>
        {content}
      </WorkspaceLayout>
      <CreateCampaignModal
        isOpen={isCampaignModalOpen}
        onClose={() => setIsCampaignModalOpen(false)}
      />
      <ManageSubscriptionDialog
        initialState={
          //rentals ALWAYS create a new subscription, existing subscriptions are irrelevant
          (manageSubscriptionState.subscriptionType !== "rental" &&
            subscriptions[manageSubscriptionState.subscriptionType]) ||
          manageSubscriptionState.subscriptionType
        }
        isOpen={manageSubscriptionState.isOpen}
        setIsOpen={() =>
          setManageSubscriptionState({
            isOpen: false,
            subscriptionType: manageSubscriptionState.subscriptionType,
          })
        }
        mixpanelSource="lock_screen"
      />
      <WebPushPrompt />
    </>
  );
}

export default function WorkspaceLayoutContainer() {
  const { workspaceId } = useParams();
  const { workspaces } = useWorkspaces();
  const location = useLocation();

  let contextWorkspaceId: string;

  if (getWhitelabelCustomization() && workspaces.length === 1) {
    contextWorkspaceId = workspaces[0].id;
  } else {
    const selectedWorkspace = workspaces.find(({ id }) => id === workspaceId);

    // Validate the workspaceId in the url
    if (!workspaceId || !selectedWorkspace) {
      return <Navigate to={`/${workspaces[0].id}`} replace />;
    }

    // Check if the current location matches the base workspace URL and redirect to "/home"
    if (location.pathname === `/workspaces/${workspaceId}`) {
      return (
        <Navigate
          to={`/workspaces/${workspaceId}/${workspaceRoutes[0].path}`}
          replace
        />
      );
    }

    contextWorkspaceId = workspaceId;
  }

  return (
    <SelectedWorkspaceProvider workspaceId={contextWorkspaceId}>
      {(props) => <Content {...props} />}
    </SelectedWorkspaceProvider>
  );
}
